import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Target,
  Plus,
  ChevronDown,
  ChevronUp,
  Edit,
  Trash2,
  Check,
  X,
  CheckCircle,
  Sparkles,
  Star,
  TrendingUp,
  AlertCircle,
  BarChart3,
  Flag
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Currency formatting helper
const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Financial Vision Form Component
const FinancialVisionForm = ({ vision, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    vision_statement: vision?.vision_statement || '',
    who_to_become: vision?.who_to_become || '',
    why_matters: vision?.why_matters || '',
    values_emotions: vision?.values_emotions?.join(', ') || '',
    what_to_avoid: vision?.what_to_avoid?.join(', ') || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
      
      const dataToSend = {
        ...formData,
        values_emotions: formData.values_emotions.split(',').map(v => v.trim()).filter(Boolean),
        what_to_avoid: formData.what_to_avoid.split(',').map(v => v.trim()).filter(Boolean)
      };
      
      await axios.post(`${API}/user/financial-vision`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onSave();
    } catch (error) {
      console.error('Error saving vision:', error);
      console.error('Error response:', error.response?.data);
      alert(error.response?.data?.detail || 'Error saving vision. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">
          {t('goals.visionStatement')}
        </label>
        <input
          type="text"
          value={formData.vision_statement}
          onChange={e => setFormData({...formData, vision_statement: e.target.value})}
          placeholder={t('goals.visionPlaceholder')}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">
          {t('goals.whoToBecome')}
        </label>
        <textarea
          value={formData.who_to_become}
          onChange={e => setFormData({...formData, who_to_become: e.target.value})}
          placeholder={t('goals.whoPlaceholder')}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          rows="3"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">
          {t('goals.whyMatters')}
        </label>
        <textarea
          value={formData.why_matters}
          onChange={e => setFormData({...formData, why_matters: e.target.value})}
          placeholder={t('goals.whyPlaceholder')}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          rows="3"
          required
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">
            {t('goals.valuesEmotions')}
          </label>
          <input
            type="text"
            value={formData.values_emotions}
            onChange={e => setFormData({...formData, values_emotions: e.target.value})}
            placeholder={t('goals.valuesPlaceholder')}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
          <p className="text-xs text-slate-500 mt-1">{t('goals.separateCommas')}</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">
            {t('goals.whatToAvoid')}
          </label>
          <input
            type="text"
            value={formData.what_to_avoid}
            onChange={e => setFormData({...formData, what_to_avoid: e.target.value})}
            placeholder={t('goals.avoidPlaceholder')}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
          <p className="text-xs text-slate-500 mt-1">{t('goals.separateCommas')}</p>
        </div>
      </div>

      <div className="flex gap-3 justify-end pt-4">
        <Button type="button" onClick={onCancel} variant="outline">
          {t('goals.cancel')}
        </Button>
        <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          {saving ? t('goals.saving') : t('goals.saveVision')}
        </Button>
      </div>
    </form>
  );
};

// Key Result Item Component
const KeyResultItem = ({ kr, objectiveId, onUpdate, isDemo }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(kr.current_value);

  const handleUpdate = () => {
    onUpdate(objectiveId, kr.kr_id, value);
    setEditing(false);
  };

  const formatValue = (val, type) => {
    if (type === 'money') return formatCurrency(val);
    if (type === 'percent') return `${val}%`;
    if (type === 'date') return val;
    return val;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-900 mb-2">{kr.description}</p>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-slate-600">
              Current: <span className="font-semibold">{formatValue(kr.current_value, kr.metric_type)}</span>
            </span>
            <span className="text-slate-600">
              Target: <span className="font-semibold">{formatValue(kr.target_value, kr.metric_type)}</span>
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-2">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-500">{kr.progress_percent.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all ${
                  kr.progress_percent >= 100 ? 'bg-green-600' :
                  kr.progress_percent >= 75 ? 'bg-emerald-600' :
                  kr.progress_percent >= 50 ? 'bg-blue-600' :
                  'bg-slate-400'
                }`}
                style={{ width: `${Math.min(kr.progress_percent, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {!isDemo && (
          <div>
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={value}
                  onChange={e => setValue(parseFloat(e.target.value) || 0)}
                  className="w-24 px-2 py-1 text-sm border border-slate-300 rounded"
                />
                <Button onClick={handleUpdate} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1">
                  <Check className="w-4 h-4" />
                </Button>
                <Button onClick={() => setEditing(false)} size="sm" variant="ghost" className="px-2 py-1">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => setEditing(true)}
                size="sm"
                variant="outline"
                className="text-xs"
              >
                {t('goals.update')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Objective Card Component
const ObjectiveCard = ({ objective, expanded, onToggle, onEdit, onDelete, onComplete, onUpdateKR }) => {
  const { t } = useTranslation();
  const isDemo = objective.is_demo;
  
  // Calculate overall progress
  const overallProgress = objective.key_results.length > 0
    ? objective.key_results.reduce((sum, kr) => sum + kr.progress_percent, 0) / objective.key_results.length
    : 0;

  const categoryColors = {
    income: 'bg-green-100 text-green-700 border-green-300',
    expenses: 'bg-red-100 text-red-700 border-red-300',
    savings: 'bg-blue-100 text-blue-700 border-blue-300',
    investments: 'bg-purple-100 text-purple-700 border-purple-300',
    debt: 'bg-orange-100 text-orange-700 border-orange-300',
    education: 'bg-indigo-100 text-indigo-700 border-indigo-300',
    mindset: 'bg-pink-100 text-pink-700 border-pink-300',
    lifestyle: 'bg-yellow-100 text-yellow-700 border-yellow-300'
  };

  const statusColors = {
    planned: 'bg-slate-100 text-slate-700',
    active: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-blue-100 text-blue-700'
  };

  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${
      isDemo ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'
    }`}>
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className={`text-lg font-bold ${isDemo ? 'text-gray-400' : 'text-slate-900'}`}>
                {objective.objective_title}
              </h3>
              {isDemo && (
                <span className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded-full font-semibold">
                  {t('goals.example') || 'Example'}
                </span>
              )}
              <span className={`px-2 py-1 text-xs rounded-full ${categoryColors[objective.category]}`}>
                {t(`goals.categories.${objective.category}`)}
              </span>
              <span className={`px-2 py-1 text-xs rounded-full ${statusColors[objective.status]}`}>
                {t(`goals.statuses.${objective.status}`)}
              </span>
            </div>
            <p className={`text-sm mb-3 ${isDemo ? 'text-gray-400' : 'text-slate-600'}`}>
              {objective.description}
            </p>
            
            {/* Progress Bar */}
            <div className="mb-2">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-600">Overall Progress</span>
                <span className="font-semibold text-slate-900">{overallProgress.toFixed(0)}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="bg-emerald-600 h-2 rounded-full transition-all"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              onClick={onToggle}
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-slate-900"
            >
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </Button>
            {!isDemo && (
              <>
                <Button
                  onClick={onEdit}
                  variant="ghost"
                  size="sm"
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                {objective.status === 'active' && overallProgress >= 100 && (
                  <Button
                    onClick={onComplete}
                    variant="ghost"
                    size="sm"
                    className="text-green-600 hover:text-green-700"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  onClick={onDelete}
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content - Key Results */}
      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-4">
          <h4 className="font-semibold text-slate-900 mb-3">{t('goals.keyResults')}</h4>
          {objective.key_results.length === 0 ? (
            <p className="text-sm text-slate-600 text-center py-4">{t('goals.noKeyResults')}</p>
          ) : (
            <div className="space-y-3">
              {objective.key_results.map(kr => (
                <KeyResultItem
                  key={kr.kr_id}
                  kr={kr}
                  objectiveId={objective.objective_id}
                  onUpdate={onUpdateKR}
                  isDemo={isDemo}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Objective Form Modal Component
const ObjectiveFormModal = ({ objective, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    objective_title: objective?.objective_title || '',
    description: objective?.description || '',
    category: objective?.category || 'savings',
    horizon: objective?.horizon || 'medium',
    status: objective?.status || 'active',
    key_results: objective?.key_results || []
  });
  const [saving, setSaving] = useState(false);

  const addKeyResult = () => {
    setFormData({
      ...formData,
      key_results: [
        ...formData.key_results,
        {
          kr_id: `temp-${Date.now()}`,
          description: '',
          metric_type: 'money',
          target_value: '',
          current_value: '',
          progress_percent: 0
        }
      ]
    });
  };

  const removeKeyResult = (index) => {
    setFormData({
      ...formData,
      key_results: formData.key_results.filter((_, i) => i !== index)
    });
  };

  const updateKeyResult = (index, field, value) => {
    const updated = [...formData.key_results];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, key_results: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
      
      if (objective) {
        // Update existing objective
        await axios.put(
          `${API}/goals/objectives/${objective.objective_id}`,
          {
            objective_title: formData.objective_title,
            description: formData.description,
            category: formData.category,
            horizon: formData.horizon,
            status: formData.status
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new objective
        const dataToSend = {
          ...formData,
          key_results: formData.key_results.map(kr => ({
            description: kr.description,
            metric_type: kr.metric_type,
            target_value: kr.target_value === '' ? 0 : parseFloat(kr.target_value) || 0,
            current_value: kr.current_value === '' ? 0 : parseFloat(kr.current_value) || 0
          }))
        };
        
        await axios.post(`${API}/goals/objectives`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      onSave();
    } catch (error) {
      console.error('Error saving objective:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMessage = error.response?.data?.detail || 'Error saving objective. Please try again.';
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            {objective ? t('goals.editObjective') : t('goals.addObjective')}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Objective Title */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                {t('goals.objectiveTitle')} *
              </label>
              <input
                type="text"
                value={formData.objective_title}
                onChange={e => setFormData({...formData, objective_title: e.target.value})}
                placeholder={t('goals.objectivePlaceholder')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                {t('goals.objectiveDescription')} *
              </label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
                placeholder={t('goals.descriptionPlaceholder')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                rows="3"
                required
              />
            </div>

            {/* Category, Horizon, Status */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  {t('goals.category')}
                </label>
                <select
                  value={formData.category}
                  onChange={e => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {['income', 'expenses', 'savings', 'investments', 'debt', 'education', 'mindset', 'lifestyle'].map(cat => (
                    <option key={cat} value={cat}>{t(`goals.categories.${cat}`)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  {t('goals.horizon')}
                </label>
                <select
                  value={formData.horizon}
                  onChange={e => setFormData({...formData, horizon: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {['short', 'medium', 'long', 'lifetime'].map(hor => (
                    <option key={hor} value={hor}>{t(`goals.horizons.${hor}`)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  {t('goals.status')}
                </label>
                <select
                  value={formData.status}
                  onChange={e => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {['planned', 'active', 'completed'].map(stat => (
                    <option key={stat} value={stat}>{t(`goals.statuses.${stat}`)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Key Results */}
            {!objective && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-semibold text-slate-700">
                    {t('goals.keyResults')}
                  </label>
                  <Button
                    type="button"
                    onClick={addKeyResult}
                    size="sm"
                    variant="outline"
                    className="text-emerald-600 border-emerald-600 hover:bg-emerald-50"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('goals.addKeyResult')}
                  </Button>
                </div>

                {formData.key_results.map((kr, index) => (
                  <div key={kr.kr_id} className="border border-slate-200 rounded-lg p-4 mb-3 bg-slate-50">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 space-y-3">
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 mb-1">
                            {t('goals.krDescription')}
                          </label>
                          <input
                            type="text"
                            value={kr.description}
                            onChange={e => updateKeyResult(index, 'description', e.target.value)}
                            placeholder={t('goals.krPlaceholder')}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white"
                            required
                          />
                        </div>
                        
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-slate-700 mb-1">
                              {t('goals.metricType')}
                            </label>
                            <select
                              value={kr.metric_type}
                              onChange={e => updateKeyResult(index, 'metric_type', e.target.value)}
                              className="w-full px-2 py-2 text-sm border border-slate-300 rounded-lg bg-white"
                            >
                              {['money', 'percent', 'milestone'].map(type => (
                                <option key={type} value={type}>{t(`goals.metricTypes.${type}`)}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-semibold text-slate-700 mb-1">
                              {t('goals.targetValue')}
                            </label>
                            <input
                              type="text"
                              value={kr.target_value || ''}
                              onChange={e => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                updateKeyResult(index, 'target_value', value);
                              }}
                              placeholder={kr.metric_type === 'money' ? '$0' : '0'}
                              className="w-full px-2 py-2 text-sm border border-slate-300 rounded-lg bg-white"
                              required
                            />
                            {kr.metric_type === 'money' && kr.target_value > 0 && (
                              <div className="text-xs text-slate-500 mt-1">
                                {formatCurrency(parseFloat(kr.target_value) || 0)}
                              </div>
                            )}
                          </div>
                          
                          <div>
                            <label className="block text-xs font-semibold text-slate-700 mb-1">
                              {t('goals.currentValue')}
                            </label>
                            <input
                              type="text"
                              value={kr.current_value || ''}
                              onChange={e => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                updateKeyResult(index, 'current_value', value);
                              }}
                              placeholder={kr.metric_type === 'money' ? '$0' : '0'}
                              className="w-full px-2 py-2 text-sm border border-slate-300 rounded-lg bg-white"
                            />
                            {kr.metric_type === 'money' && kr.current_value > 0 && (
                              <div className="text-xs text-slate-500 mt-1">
                                {formatCurrency(parseFloat(kr.current_value) || 0)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        type="button"
                        onClick={() => removeKeyResult(index)}
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 mt-6"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button type="button" onClick={onCancel} variant="outline">
                {t('goals.cancel')}
              </Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {saving ? t('goals.saving') : objective ? t('actionPlan.updateTask') : t('actionPlan.saveTask')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Task Form Modal Component
export const TaskFormModal = ({ task, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: task?.title || '',
    description: task?.description || '',
    category: task?.category || 'income_growth',
    due_date: task?.due_date ? task.due_date.split('T')[0] : new Date().toISOString().split('T')[0],
    priority: task?.priority || 'medium',
    estimated_impact: task?.estimated_impact || '',
    linked_goal: task?.linked_goal || ''
  });
  const [availableGoals, setAvailableGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(true);

  // Fetch available goals
  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
        const response = await axios.get(`${API}/goals/objectives`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Fetched goals for dropdown:', response.data.objectives);
        setAvailableGoals(response.data.objectives || []);
      } catch (error) {
        console.error('Error fetching goals:', error);
        console.error('Error details:', error.response?.data);
        setAvailableGoals([]);
      } finally {
        setLoadingGoals(false);
      }
    };
    fetchGoals();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.due_date) {
      toast.error(t('actionPlan.fillRequired'));
      return;
    }

    const submitData = {
      ...formData,
      estimated_impact: parseFloat(formData.estimated_impact) || 0,
      due_date: new Date(formData.due_date + 'T00:00:00.000Z').toISOString()
    };

    onSave(submitData);
  };

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              {task ? t('actionPlan.editTask') : t('actionPlan.addTask')}
            </h2>
            <Button variant="outline" size="sm" onClick={onCancel}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <p className="text-gray-600 mb-4">
            {t('actionPlan.formDescription')}
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">{t('actionPlan.taskTitle')} *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder={t('actionPlan.titlePlaceholder')}
                required
              />
            </div>

            <div>
              <Label htmlFor="description">{t('actionPlan.taskDescription')}</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('actionPlan.descriptionPlaceholder')}
              />
            </div>

            <div>
              <Label htmlFor="category">{t('actionPlan.category')} *</Label>
              <Select value={formData.category} onValueChange={(value) => handleChange('category', value)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('actionPlan.selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="the_one_thing">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4" />
                      {t('actionPlan.categories.the_one_thing')}
                    </div>
                  </SelectItem>
                  <SelectItem value="income_growth">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      {t('actionPlan.categories.income_growth')}
                    </div>
                  </SelectItem>
                  <SelectItem value="loss_avoidance">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {t('actionPlan.categories.loss_avoidance')}
                    </div>
                  </SelectItem>
                  <SelectItem value="managing_investments">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      {t('actionPlan.categories.managing_investments')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="due_date">{t('actionPlan.dueDate')} *</Label>
              <Input
                id="due_date"
                type="date"
                value={formData.due_date}
                onChange={(e) => handleChange('due_date', e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="priority">{t('actionPlan.priority')} *</Label>
              <Select value={formData.priority} onValueChange={(value) => handleChange('priority', value)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('actionPlan.selectPriority')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    <div className="flex items-center gap-2">
                      <Flag className="w-4 h-4 text-green-600" />
                      {t('actionPlan.priorities.low')}
                    </div>
                  </SelectItem>
                  <SelectItem value="medium">
                    <div className="flex items-center gap-2">
                      <Flag className="w-4 h-4 text-yellow-600" />
                      {t('actionPlan.priorities.medium')}
                    </div>
                  </SelectItem>
                  <SelectItem value="high">
                    <div className="flex items-center gap-2">
                      <Flag className="w-4 h-4 text-red-600" />
                      {t('actionPlan.priorities.high')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="linked_goal">{t('actionPlan.linkedGoal')}</Label>
              <Select value={formData.linked_goal || 'none'} onValueChange={(value) => handleChange('linked_goal', value === 'none' ? '' : value)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('actionPlan.selectGoal')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <div className="flex items-center gap-2">
                      <X className="w-4 h-4 text-gray-400" />
                      {t('actionPlan.noGoal')}
                    </div>
                  </SelectItem>
                  {loadingGoals ? (
                    <SelectItem value="loading" disabled>
                      <span className="text-gray-400">Loading goals...</span>
                    </SelectItem>
                  ) : (
                    availableGoals
                      .filter(goal => goal.status === 'active' || goal.status === 'planned')
                      .map(goal => (
                        <SelectItem key={goal.objective_id} value={goal.objective_id}>
                          <div className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-emerald-600" />
                            <span className="truncate">{goal.objective_title}</span>
                          </div>
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {t('actionPlan.linkedGoalNote') || 'Link this task to a goal (optional)'}
              </p>
            </div>

            <div>
              <Label htmlFor="estimated_impact">{t('actionPlan.estimatedImpact')}</Label>
              <Input
                id="estimated_impact"
                type="number"
                step="1"
                value={formData.estimated_impact}
                onChange={(e) => handleChange('estimated_impact', e.target.value)}
                placeholder="500"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('actionPlan.impactNote')}
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onCancel}>
                {t('actionPlan.cancel')}
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {task ? t('actionPlan.updateTask') : t('actionPlan.saveTask')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Main GoalsModule Component
const GoalsModule = () => {
  const { t } = useTranslation();
  const [objectives, setObjectives] = useState([]);
  const [financialVision, setFinancialVision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVisionForm, setShowVisionForm] = useState(false);
  const [showObjectiveForm, setShowObjectiveForm] = useState(false);
  const [editingObjective, setEditingObjective] = useState(null);
  const [expandedObjectives, setExpandedObjectives] = useState(new Set());

  // Fetch data
  useEffect(() => {
    fetchGoalsData();
  }, []);

  const fetchGoalsData = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
      
      const [objectivesRes, visionRes] = await Promise.all([
        axios.get(`${API}/goals/objectives`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/user/financial-vision`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setObjectives(objectivesRes.data.objectives || []);
      setFinancialVision(visionRes.data.vision);
      
      // Auto-expand active objectives
      const activeIds = new Set(
        (objectivesRes.data.objectives || [])
          .filter(obj => obj.status === 'active')
          .map(obj => obj.objective_id)
      );
      setExpandedObjectives(activeIds);
    } catch (error) {
      console.error('Error fetching goals data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleObjective = (objectiveId) => {
    setExpandedObjectives(prev => {
      const newSet = new Set(prev);
      if (newSet.has(objectiveId)) {
        newSet.delete(objectiveId);
      } else {
        newSet.add(objectiveId);
      }
      return newSet;
    });
  };

  const handleUpdateKRProgress = async (objectiveId, krId, currentValue) => {
    try {
      const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
      await axios.put(
        `${API}/goals/key-results/${objectiveId}/${krId}`,
        { current_value: parseFloat(currentValue) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchGoalsData();
    } catch (error) {
      console.error('Error updating key result:', error);
    }
  };

  const handleDeleteObjective = async (objectiveId) => {
    if (window.confirm(t('goals.confirmDelete'))) {
      try {
        const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
        await axios.delete(`${API}/goals/objectives/${objectiveId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await fetchGoalsData();
      } catch (error) {
        console.error('Error deleting objective:', error);
      }
    }
  };

  const handleCompleteObjective = async (objectiveId) => {
    try {
      const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
      await axios.put(
        `${API}/goals/objectives/${objectiveId}`,
        { status: 'completed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchGoalsData();
    } catch (error) {
      console.error('Error completing objective:', error);
    }
  };

  // Calculate progress overview
  const activeObjectives = objectives.filter(obj => obj.status === 'active');
  const avgProgress = activeObjectives.length > 0
    ? activeObjectives.reduce((sum, obj) => {
        const objProgress = obj.key_results.length > 0
          ? obj.key_results.reduce((s, kr) => s + kr.progress_percent, 0) / obj.key_results.length
          : 0;
        return sum + objProgress;
      }, 0) / activeObjectives.length
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">{t('goals.loadingGoals')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-emerald-50 to-blue-50 border-emerald-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-3xl font-bold text-slate-900 flex items-center gap-2">
                <Target className="w-8 h-8 text-emerald-600" />
                {t('goals.title')}
              </CardTitle>
              <p className="text-slate-600 mt-2">{t('goals.description')}</p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Progress Overview */}
      {activeObjectives.length > 0 && (
        <Card className="border-emerald-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">
              {t('goals.progressOverview')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm text-slate-600">{t('goals.activeObjectives')}</div>
                <div className="text-3xl font-bold text-blue-600">{activeObjectives.length}</div>
              </div>
              <div className="bg-emerald-50 p-4 rounded-lg">
                <div className="text-sm text-slate-600">{t('goals.avgProgress')}</div>
                <div className="text-3xl font-bold text-emerald-600">{avgProgress.toFixed(0)}%</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-sm text-slate-600">{t('goals.totalKeyResults')}</div>
                <div className="text-3xl font-bold text-purple-600">
                  {activeObjectives.reduce((sum, obj) => sum + obj.key_results.length, 0)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Financial Vision Card */}
      <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-amber-500" />
                {t('goals.visionTitle')}
              </CardTitle>
              <p className="text-sm text-slate-600 mt-1">{t('goals.visionSubtitle')}</p>
            </div>
            <Button
              onClick={() => setShowVisionForm(!showVisionForm)}
              variant="outline"
              size="sm"
              className="border-amber-300 hover:bg-amber-100"
            >
              {financialVision ? t('goals.editVision') : t('goals.createVision')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showVisionForm ? (
            <FinancialVisionForm
              vision={financialVision}
              onSave={() => {
                fetchGoalsData();
                setShowVisionForm(false);
              }}
              onCancel={() => setShowVisionForm(false)}
            />
          ) : financialVision ? (
            <div className="space-y-4">
              <div>
                <div className="text-lg font-semibold text-slate-900 italic">
                  "{financialVision.vision_statement}"
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-semibold text-slate-700">Who I Want to Become:</span>
                  <p className="text-slate-600 mt-1">{financialVision.who_to_become}</p>
                </div>
                <div>
                  <span className="font-semibold text-slate-700">Why It Matters:</span>
                  <p className="text-slate-600 mt-1">{financialVision.why_matters}</p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-slate-600 text-center py-4">
              {t('goals.startVisionMessage')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Objectives Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-bold text-slate-900">
              {t('goals.objectivesTitle')}
            </CardTitle>
            <Button
              onClick={() => {
                setEditingObjective(null);
                setShowObjectiveForm(true);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('goals.addObjective')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {objectives.length === 0 ? (
            <div className="text-center py-12">
              <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">{t('goals.noObjectives')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {objectives.map(objective => (
                <ObjectiveCard
                  key={objective.objective_id}
                  objective={objective}
                  expanded={expandedObjectives.has(objective.objective_id)}
                  onToggle={() => toggleObjective(objective.objective_id)}
                  onEdit={() => {
                    setEditingObjective(objective);
                    setShowObjectiveForm(true);
                  }}
                  onDelete={() => handleDeleteObjective(objective.objective_id)}
                  onComplete={() => handleCompleteObjective(objective.objective_id)}
                  onUpdateKR={handleUpdateKRProgress}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Objective Form Modal */}
      {showObjectiveForm && (
        <ObjectiveFormModal
          objective={editingObjective}
          onSave={() => {
            fetchGoalsData();
            setShowObjectiveForm(false);
            setEditingObjective(null);
          }}
          onCancel={() => {
            setShowObjectiveForm(false);
            setEditingObjective(null);
          }}
        />
      )}
    </div>
  );
};

export default GoalsModule;
